// Revenue Ops — daily digest + three high-ROI lists (upsell / churn-risk / win-back).
// All filters run off the same contact snapshot; auto-tasks are dedup'd via autoKey.
import { useState, useEffect, useMemo, useRef } from "react";
import { Avatar, HealthBadge, LeadBadge, StageBadge, PlanBadge, timeAgo } from "../components/UI";
import { watchContacts } from "../lib/db";
import { classifyRevenueSignals, winBackPriority } from "../lib/healthScore";
import { runAutoTaskTriggers, triggersForContact } from "../lib/revenueOps";
import PageTabs from "../components/PageTabs";
import KpiCard from "../components/charts/KpiCard";
import AlertBanner from "../components/AlertBanner";
import EmptyState from "../components/EmptyState";
import { TrendingUp, AlertTriangle, RotateCcw, ListTodo } from "lucide-react";

const TABS = [
  { id: "upsell", label: "Upsell-Ready", description: "Paid users hitting plan limits with no payment issues. Pitch next tier." },
  { id: "churn", label: "Churn-Risk", description: "Paying customers whose engagement has dropped or whose card is failing." },
  { id: "winback", label: "Win-Back", description: "Churned customers ranked by LTV with recency decay." },
];

function fmtUsd(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function Revenue({ onOpenContact }) {
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("upsell");
  const [err, setErr] = useState(null);
  const [autoTaskRun, setAutoTaskRun] = useState({ ran: false, count: 0 });
  const ranOnceRef = useRef(false);

  useEffect(() => {
    return watchContacts(
      (rows) => {
        setErr(null);
        setContacts(rows);
      },
      () => setErr("Could not load contacts.")
    );
  }, []);

  // One auto-task evaluation per session — cheap, non-blocking, capped.
  useEffect(() => {
    if (ranOnceRef.current) return;
    if (!contacts.length) return;
    ranOnceRef.current = true;
    if (typeof window !== "undefined" && sessionStorage.getItem("revenueOps:ranAutoTasks") === "1") {
      return;
    }
    runAutoTaskTriggers(contacts, { maxPerRun: 25 })
      .then((count) => {
        try { sessionStorage.setItem("revenueOps:ranAutoTasks", "1"); } catch {}
        setAutoTaskRun({ ran: true, count });
      })
      .catch(() => {});
  }, [contacts]);

  const enriched = useMemo(
    () => contacts
      .filter((c) => !c.isInternal)
      .map((c) => ({ ...c, _signals: classifyRevenueSignals(c), _winBack: winBackPriority(c) })),
    [contacts]
  );

  const upsell = useMemo(
    () => enriched
      .filter((c) => c._signals.isUpsellReady)
      .sort((a, b) => (b.leadScore ?? 0) - (a.leadScore ?? 0)),
    [enriched]
  );
  const churn = useMemo(
    () => enriched
      .filter((c) => c._signals.isChurnRisk)
      .sort((a, b) => (a.healthScore ?? 0) - (b.healthScore ?? 0)),
    [enriched]
  );
  const winback = useMemo(
    () => enriched
      .filter((c) => c._signals.isWinBackTarget)
      .sort((a, b) => b._winBack - a._winBack),
    [enriched]
  );

  const triggerCount = useMemo(
    () => enriched.reduce((acc, c) => acc + triggersForContact(c).length, 0),
    [enriched]
  );

  const totalMrrAtRisk = useMemo(
    () => churn.reduce((s, c) => s + (Number(c.mrr) || 0), 0),
    [churn]
  );
  const totalLtvWinBack = useMemo(
    () => winback.reduce((s, c) => s + (Number(c.lifetimeRevenue) || 0), 0),
    [winback]
  );

  const visible =
    tab === "upsell" ? upsell :
    tab === "churn" ? churn :
    winback;

  const tabMeta = TABS.find((t) => t.id === tab);

  const tabItems = TABS.map((t) => ({
    id: t.id,
    label: t.label,
    count: { upsell: upsell.length, churn: churn.length, winback: winback.length }[t.id],
  }));

  return (
    <div className="page-wrap" style={{ maxWidth: 1100 }}>
      <header className="page-header">
        <h1 className="page-header-title">Revenue Ops</h1>
        <p className="page-header-desc">
          Today&apos;s queue — who to talk to, and why. Lists derive from contact state; matching auto-tasks are deduped by week / month.
        </p>
      </header>

      {err && <AlertBanner variant="error">{err}</AlertBanner>}

      <div className="stats-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", marginBottom: 18 }}>
        <KpiCard label="Upsell-ready" icon={TrendingUp} value={String(upsell.length)} comparison="Paid users near plan limits" />
        <KpiCard label="Churn-risk" icon={AlertTriangle} accent="warning" value={String(churn.length)} comparison={`${fmtUsd(totalMrrAtRisk)} MRR`} />
        <KpiCard label="Win-back" icon={RotateCcw} value={String(winback.length)} comparison={`${fmtUsd(totalLtvWinBack)} LTV`} />
        <KpiCard
          label="Auto-tasks pending"
          icon={ListTodo}
          value={String(triggerCount)}
          comparison={autoTaskRun.ran ? "synced this session" : "evaluating…"}
        />
      </div>

      <PageTabs tabs={tabItems} value={tab} onChange={setTab} />
      <p className="text-caption" style={{ margin: "0 0 16px" }}>{tabMeta?.description}</p>

      <div className="card contacts-table-card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Contact</th>
                <th>Plan</th>
                <th>Stage</th>
                {tab === "upsell" && <th>Lead</th>}
                {tab === "churn" && <th>Health</th>}
                {tab === "winback" && <th>Priority</th>}
                <th>{tab === "upsell" ? "Usage" : tab === "churn" ? "MRR" : "LTV"}</th>
                <th>Last active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={ListTodo}
                      title="Nothing here today"
                      description={
                        tab === "upsell"
                          ? "No paying users near plan limits."
                          : tab === "churn"
                            ? "All paying customers look healthy."
                            : "No churned customers with revenue history."
                      }
                    />
                  </td>
                </tr>
              ) : (
                visible.map((c) => (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => onOpenContact(c)}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={c.name} size="sm" />
                        <div>
                          <div style={{ fontWeight: 500 }}>{c.name || c.email || c.id}</div>
                          <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><PlanBadge plan={c.plan || "Free"} /></td>
                    <td><StageBadge stage={c.stage} /></td>
                    {tab === "upsell" && <td><LeadBadge score={c.leadScore} /></td>}
                    {tab === "churn" && <td><HealthBadge score={c.healthScore} /></td>}
                    {tab === "winback" && <td style={{ fontWeight: 500 }}>{fmtUsd(c._winBack)}</td>}
                    <td style={{ fontSize: 13 }}>
                      {tab === "upsell"
                        ? `${Math.round(Number(c.usagePctOfLimit) || 0)}%`
                        : tab === "churn"
                          ? `${fmtUsd(c.mrr || 0)}/mo`
                          : fmtUsd(c.lifetimeRevenue || 0)}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>
                      {c.lastActiveAt ? timeAgo(c.lastActiveAt) : "—"}
                    </td>
                    <td>
                      <button type="button" className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); onOpenContact(c); }}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p style={{ fontSize: 11, color: "var(--text-3)", marginTop: 14, lineHeight: 1.55 }}>
        Auto-tasks are written to the <code>tasks</code> collection with an <code>autoKey</code> field
        (e.g. <code>upsell:&lt;uid&gt;:2026-04</code>) so the same trigger never creates a duplicate within
        a week (payment fail) or month (upsell / churn-risk / win-back). Clear
        <code> sessionStorage["revenueOps:ranAutoTasks"]</code> to re-run in the same session.
      </p>
    </div>
  );
}

